import { useNavigate, useParams } from 'react-router-dom';

import React from 'react';
import { reverse } from 'named-urls';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';
import { Drawer } from '@mui/material';

import routes from 'core/routes';

import Grid, { defaultRenderers } from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';

import { TaskStatus } from 'utils/api/modules/tasks/tasks.constants';

import { TaskStatusOperator } from 'components/Grid/operators/Status/';

import { ExtendedUserOperators } from 'components/Grid/operators/User/User';

import { useStore } from 'utils/hooks/store';

import { getEntityGridColumns } from 'components/Grid/components/EntitiesGridColumn/EntitiesGridColumn';

import ScheduleDrawer from './ScheduleDrawer';

import { ActionsSchedulesTableProps } from '../TasksTable/types';
import { StyledTaskChip, StyledTaskDueDate } from '../TasksTable/styles';
import ActionCell from '../ActionCell';
import { NameCellContainer, NameText } from './styles';

export type ScheduleItemType = 'task' | 'audit';

export interface ScheduleItem {
  id?: string;
  uuid?: string;
  type: ScheduleItemType;
  name?: string;
  template?: {
    name: string;
  };
  schedule?: {
    uuid: string;
    name: string;
  };
}

export const getItemName = (row: ScheduleItem): string => {
  if (row.type === 'audit') {
    return row.schedule?.name || '';
  }
  return row.name || '';
};

const NameCell = ({ row }: { row: ScheduleItem }): React.ReactElement => {
  const name = getItemName(row);

  return (
    <NameCellContainer>
      <NameText>{name}</NameText>
    </NameCellContainer>
  );
};

const nameColumn = {
  field: 'name',
  headerName: 'Name',
  nativeFilter: true,
  renderCell: ({ row }: { row: ScheduleItem }) => <NameCell row={row} />,
  valueGetter: (_value: unknown, row: ScheduleItem) => getItemName(row),
};

const typeColumn = {
  field: 'type',
  headerName: 'Type',
  renderCell: ({ value }: { value: string }) => (
    <NameCellContainer>
      <NameText>{value.charAt(0).toUpperCase() + value.slice(1)}</NameText>
    </NameCellContainer>
  ),
  nativeFilter: true,
};

const assignedToColumn = {
  field: 'assignedTo',
  headerName: 'Assigned to',
  renderCell: defaultRenderers.user,
  filterOperators: ExtendedUserOperators(),
  valueGetter: (_value: unknown, row: ScheduleItem) => ({
    value:
      row.type === 'audit'
        ? (row as unknown as { assignedTo: unknown }).assignedTo
        : (row as unknown as { assigned: unknown }).assigned,
  }),
  nativeFilter: true,
};

const statusColumn = {
  field: 'status',
  headerName: 'Status',
  renderCell: ({ value }: { value: TaskStatus }) => <StyledTaskChip status={value} />,
  filterOperators: TaskStatusOperator(),
  nativeFilter: true,
};

const dueDateColumn = {
  field: 'dueDate',
  headerName: 'Due date',
  renderCell: ({ value, row }: { value: string; row: ScheduleItem & { status?: TaskStatus } }) => (
    <StyledTaskDueDate date={value} status={row.status} showTime />
  ),
  nativeFilter: true,
};

const actionsColumn = {
  field: 'actions',
  type: 'actions',
  headerName: '',
  maxWidth: 50,
  ariaLabel: 'actions_cell',
  renderCell: ({ row, colDef }: { row: ScheduleItem; colDef: ColumnDefinition }) => (
    <ActionCell rowData={row} actions={colDef.actions} />
  ),
  filterOperators: null,
  sortable: false,
};

const startDateColumn = {
  field: 'startDate',
  headerName: 'Start date',
  nativeFilter: true,
  defaultVisibility: false,
};

export const getTasksTableBasicColumns = (): ColumnDefinition[] =>
  [
    nameColumn,
    assignedToColumn,
    statusColumn,
    dueDateColumn,
    startDateColumn,
    actionsColumn,
  ] as unknown as ColumnDefinition[];

export const tasksTableBasicColumns = [
  nameColumn,
  typeColumn,
  assignedToColumn,
  statusColumn,
  dueDateColumn,
  startDateColumn,
  actionsColumn,
] as unknown as ColumnDefinition[];

const ActionsSchedulesTable = ({
  onError,
  onRefreshCallback,
  gridApiRef,
  temporaryFilters,
  initialFilters,
}: ActionsSchedulesTableProps): React.ReactElement => {
  const navigate = useNavigate();
  const { scheduleId, instanceId } = useParams<{ scheduleId?: string; instanceId?: string }>();
  const refreshFnRef = React.useRef<(() => void) | null>(null);

  const isDrawerOpen = !!(scheduleId && instanceId);

  const onRowClick: GridEventListener<'rowClick'> = (params: GridRowParams<ScheduleItem>): void => {
    if (params.row.type === 'audit') {
      navigate(
        reverse(routes.dashboard.tasks.scheduleInstance, {
          scheduleId: params.row.schedule?.uuid as string,
          instanceId: params.row.id as string,
        })
      );
    } else {
      navigate(reverse(routes.dashboard.tasks.details, { taskId: params.row.uuid as string }));
    }
  };

  const onCloseDrawer = (): void => {
    navigate(routes.dashboard.tasks.audits.toString());
  };

  const handleRefreshCallback = (refreshFn: () => void): void => {
    refreshFnRef.current = refreshFn;
    onRefreshCallback?.(refreshFn);
  };

  const {
    session: { user },
  } = useStore();

  const basicColumns = React.useMemo(
    () => [...getTasksTableBasicColumns(), ...getEntityGridColumns(user?.activeEntityModules || [])],
    [user?.activeEntityModules]
  );

  return (
    <>
      <Grid
        basicColumns={basicColumns}
        entity="action"
        onError={onError}
        onRefreshCallback={handleRefreshCallback}
        onRowClick={onRowClick}
        dataURL="/api/schedules/tasks/?type=audit&withPagination=true"
        sortBy={{ field: 'dueDate', sort: 'asc' }}
        customNoRowsText="No audits match the selected filters"
        disableColumnMenu
        disableColumnSelector
        disableColumnFilter
        disableSavePreferences
        gridApiRef={gridApiRef}
        getRowId={(row) => row.uuid ?? row.id}
        showHeader={false}
        temporaryFilters={temporaryFilters}
        filters={initialFilters}
      />
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={onCloseDrawer}
        PaperProps={{
          sx: {
            minWidth: '35%',
            maxWidth: '35%',
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
            backgroundColor: '#fff',
          },
        }}
      >
        {isDrawerOpen && (
          <ScheduleDrawer
            itemId={instanceId}
            scheduleId={scheduleId}
            onClose={onCloseDrawer}
            onUpdate={() => refreshFnRef.current?.()}
          />
        )}
      </Drawer>
    </>
  );
};

export default ActionsSchedulesTable;
